import {
  useAssignmentApproveMultipleAssignmentsWithAutoApprovalConditions,
  useAssignmentGetExtendedAssignments,
} from 'api/assignment/assignment';
import { useCaseAddOrUpdateComment } from 'api/case/case';
import {
  AssignmentExtendedModel,
  AssignmentWithAutoApprovalCondition,
  CaseTypeEnum,
  ExpenseType,
} from 'api/model';
import Modal from 'components/Modal';
import useModalStack from 'contexts/modal/useModalStack';
import { useCallback, useMemo, useState } from 'react';

export type MissingExpenses = {
  assignment: AssignmentExtendedModel;
  caseType: CaseTypeEnum;
  missingExpenses: ExpenseType[];
};

const useAutoapproveAssignmentsBulkOperations = (
  allAssignments: AssignmentWithAutoApprovalCondition[],
  // Alla uppdrag på sidan som får godkännas manuellt (ej redan godkända +
  // godkännandespärren upplåst) — oavsett autogodkännbarhetsstatus.
  selectableAssignments: AssignmentWithAutoApprovalCondition[],
  onAssignmentsUpdated: (approvedAssignmentIds?: number[]) => void
) => {
  const modalstack = useModalStack();
  // get the extended assignments (including expected expenses, etc) for the selected assignments
  const getExtendedAssignmentCall = useAssignmentGetExtendedAssignments();

  const approveCall =
    useAssignmentApproveMultipleAssignmentsWithAutoApprovalConditions();
  const sendMessage = useCaseAddOrUpdateComment();
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState(
    new Set<number>()
  );

  // Delmängden av de valbara som dessutom är direkt godkännbara (autogodkännbara).
  const autoApprovableSelectableAssignments = useMemo(
    () => selectableAssignments.filter((x) => x.result?.autoApprove === true),
    [selectableAssignments]
  );

  const selectedAssignments = useMemo((): Record<
    number,
    AssignmentWithAutoApprovalCondition
  > => {
    const result: Record<number, AssignmentWithAutoApprovalCondition> = {};
    selectableAssignments.forEach((a) => {
      if (selectedAssignmentIds.has(a.assignment.assignment.assignmentID)) {
        result[a.assignment.assignment.assignmentID] = a;
      }
    });
    return result;
  }, [selectedAssignmentIds, selectableAssignments]);

  const selectedApprovableAssignments = useMemo(
    () =>
      selectableAssignments.filter((avm) =>
        selectedAssignmentIds.has(avm.assignment.assignment.assignmentID)
      ),
    // .filter((a) => validateMoveToApproved(a).length === 0),
    [selectableAssignments, selectedAssignmentIds]
  );

  const toggleSelectAssignment = useCallback((assignmentId: number) => {
    setSelectedAssignmentIds((ids) => {
      const newIds = new Set(ids);
      if (newIds.has(assignmentId)) {
        newIds.delete(assignmentId);
      } else {
        newIds.add(assignmentId);
      }

      return newIds;
    });
  }, []);

  // Väljer ALLA valbara uppdrag på sidan, oavsett autogodkännbarhetsstatus —
  // även uppdrag med avvikelser eller som inte kontrollerats än.
  const selectAllAssignments = useCallback(() => {
    const newSelectedAssignmentIds = new Set(
      selectableAssignments.map((a) => a.assignment.assignment.assignmentID)
    );
    setSelectedAssignmentIds(newSelectedAssignmentIds);
  }, [selectableAssignments]);

  const selectAllApprovableAssignments = useCallback(() => {
    const newSelectedAssignmentIds = new Set(
      autoApprovableSelectableAssignments.map(
        (a) => a.assignment.assignment.assignmentID
      )
    );
    setSelectedAssignmentIds(newSelectedAssignmentIds);
  }, [autoApprovableSelectableAssignments]);

  const approveSelectedAssignments = useCallback(async () => {
    const toApprove = allAssignments.filter((avm) =>
      selectedAssignmentIds.has(avm.assignment.assignment.assignmentID)
    );
    const assignmentIdsToApprove = Array.from(selectedAssignmentIds);
    
    try {
      const result = await approveCall.mutateAsync({
        data: toApprove,
      });
      if (result?.length) {
        const modalId = modalstack.push(
          <Modal
            buttons={[{ label: 'Ok', onClick: () => modalstack.pop(modalId) }]}
            onClose={() => modalstack.pop(modalId)}
            title="Fel"
          >
            Det gick inte att godkänna {result.length} av {toApprove.length}{' '}
            uppdrag:
            <ul>
              {result.map((errorMessage) => (
                <li key={errorMessage}>{errorMessage}</li>
              ))}
            </ul>
          </Modal>
        );
        // Pass the IDs that were successfully approved (all except the failed ones).
        // The backend error strings contain the assignment ID ("FEL: Kunde inte
        // godkänna uppdrag <id>: ..."), so substring-match against them.
        const successfullyApprovedIds = assignmentIdsToApprove.filter(
          (id) =>
            !result.some((errorMessage) =>
              errorMessage.includes(`uppdrag ${id}:`)
            )
        );
        onAssignmentsUpdated(successfullyApprovedIds);
      } else {
        // All assignments were successfully approved
        onAssignmentsUpdated(assignmentIdsToApprove);
        setSelectedAssignmentIds(new Set());
      }
    } catch (error) {
      // do nothing, the error will be handled by the mutation's onError handler
    }
  }, [
    allAssignments,
    selectedAssignmentIds,
    approveCall,
    modalstack,
    onAssignmentsUpdated,
  ]);

  const messageSelectedAssignments = useCallback(
    async (message: string) => {
      const assignmentsToMessage = selectableAssignments.filter((a) =>
        selectedAssignmentIds.has(a.assignment.assignment.assignmentID)
      );

      await assignmentsToMessage.forEach((assignment) =>
        sendMessage.mutateAsync(
          {
            params: {
              caseId: assignment.assignment.case.caseID,
              message,
              readByAdmin: true,
            },
          },
          {
            onError(error, variables) {
              const modalId = modalstack.push(
                <Modal
                  buttons={[
                    { label: 'Ok', onClick: () => modalstack.pop(modalId) },
                  ]}
                  onClose={() => modalstack.pop(modalId)}
                  title="Fel"
                >
                  Något gick fel med att skicka meddelande till uppdrag{' '}
                  {variables.params?.caseId}. Försök igen.
                </Modal>
              );
            },
          }
        )
      );

      setSelectedAssignmentIds(new Set());
    },
    [modalstack, selectableAssignments, selectedAssignmentIds, sendMessage]
  );

  const checkExpectedExpenses = useCallback(async () => {
    // first, fetch the extended assignments
    try {
      const extendedAssignmentsList =
        await getExtendedAssignmentCall.mutateAsync({
          data: Array.from(selectedAssignmentIds),
        });
      if (!extendedAssignmentsList) {
        return [];
      }
      // then, check for missing expenses
      // const response = selectableAssignments
      //   .filter((sa) => selectedAssignmentIds.has(sa.assignment.assignmentID))
      const response = extendedAssignmentsList.reduce((acc, cur) => {
        const missingExpenses = cur.assignment.expectedExpenses.reduce(
          (a, c) => {
            if (
              cur.expenses.some(
                (e) => e.expenseType === c.type && e.approvedBy !== null
              )
            ) {
              return a;
            }

            return [...a, c.type];
          },
          [] as ExpenseType[]
        );

        return [
          ...acc,
          {
            missingExpenses,
            assignment: cur,
            caseType: cur.case.caseTypeID,
          } as MissingExpenses,
        ];
      }, [] as MissingExpenses[]);

      return response;
    } catch (error) {
      return [];
    }
  }, [getExtendedAssignmentCall, selectedAssignmentIds]);

  return useMemo(
    () => ({
      clearSelectedAssignments: () => setSelectedAssignmentIds(new Set()),
      selectedAssignmentIds,
      selectedAssignments,
      toggleSelectAssignment,
      selectAllAssignments,
      selectedApprovableAssignments,
      selectAllApprovableAssignments,
      approveSelectedAssignments,
      approveStatus: approveCall.status,
      messageSelectedAssignments,
      sendMessageStatus: sendMessage.status,
      checkExpectedExpenses,
      selectableAssignments,
      autoApprovableSelectableAssignments,
    }),
    [
      selectedAssignmentIds,
      selectedAssignments,
      toggleSelectAssignment,
      selectAllAssignments,
      selectedApprovableAssignments,
      selectAllApprovableAssignments,
      approveSelectedAssignments,
      approveCall.status,
      messageSelectedAssignments,
      sendMessage.status,
      checkExpectedExpenses,
      selectableAssignments,
      autoApprovableSelectableAssignments,
    ]
  );
};

export default useAutoapproveAssignmentsBulkOperations;
